import { useState } from "react";
import { ArrowUpRight, Bookmark, Eye, Heart, Info, Link2, MessageCircle, RefreshCw, Share2 } from "lucide-react";
import { Button } from "@/components/form/Button";
import { cn } from "@/utils/helpers";
import { KitModal } from "../../components/kit/Modal";
import {
  channelName,
  compactNumber,
  metricList,
  metricsSourceLabel,
  numbersLine,
  PLATFORM_NAME,
  postStatus,
  toneClass,
  type MetricKind,
} from "./liveModel";
import type { LiveChannel, LiveData, LivePost } from "./liveTypes";
import LiveNumbersChart, { type ChartPlatform } from "./LiveNumbersChart";
import { MarkWell } from "./LiveParts";

const METRIC_ICON: Record<MetricKind, typeof Eye> = { views: Eye, likes: Heart, saves: Bookmark, comments: MessageCircle, shares: Share2 };
const METRIC_WORD: Record<MetricKind, string> = { views: "Views", likes: "Likes", saves: "Saves", comments: "Comments", shares: "Shares" };

/**
 * The platforms the chart draws, in the post's channel order. Numbers are
 * read per platform, so Instagram's feed and Reel would share one line; a
 * platform with one channel is named as the channel ("Instagram Reel").
 */
export function chartPlatforms(data: LiveData, only?: string): ChartPlatform[] {
  const order = [...new Set(data.channels.map((channel) => channel.platform))];
  return order.flatMap((platform) => {
    if (only && platform !== only) return [];
    const channels = data.channels.filter((channel) => channel.platform === platform);
    const ids = new Set(channels.map((channel) => channel.id));
    const posts = data.posts.filter((post) => ids.has(post.channelId) && post.liveSince);
    if (!posts.length) return [];
    const metricsUpdatedAt = posts.map((post) => post.metricsUpdatedAt ?? "").sort().pop() || null;
    // The platform went live with its first channel.
    const liveSince = posts.map((post) => post.liveSince!).sort()[0];
    const label = channels.length === 1 ? channelName(channels[0]) : (PLATFORM_NAME[platform] ?? platform);
    return [{ platform, label, metricsUpdatedAt, liveSince }];
  });
}

/**
 * One live post's numbers, opened from its "Live since": the numbers now, the
 * chart for its platform alone, Open, Refresh, and Change link where the link
 * can change (a pasted one, or one whose numbers can't be read).
 */
export default function LivePostDialog({
  slotId,
  data,
  channel,
  post,
  timeZone,
  onClose,
  onRefresh,
  onSetLink,
}: {
  slotId: string;
  data: LiveData;
  channel: LiveChannel;
  post: LivePost;
  timeZone: string;
  onClose: () => void;
  onRefresh?: () => void;
  /** Saves the link to the channel's post. Absent for people who can't. */
  onSetLink?: (url: string) => void;
}) {
  const name = channelName(channel);
  const status = postStatus(post, { kitSent: true, timeZone, withDay: true });
  const numbers = metricList(post.metrics);
  const line = numbersLine(post, { timeZone });
  const source = metricsSourceLabel(post.metricsSource);
  const reading = !!post.tracking?.reading;
  const error = post.tracking && !reading ? post.tracking.error : null;
  // The Kit editor's rule: a link CreativeOS posted is changed only once it can't be read.
  const canChange = !!onSetLink && (post.postedVia !== "CREATIVEOS" || !!error);
  const platform = PLATFORM_NAME[channel.platform] ?? channel.platform;

  return (
    <KitModal isOpen onOpenChange={(open) => !open && onClose()} aria-label={`The ${name} post's numbers`} modalClassName="max-w-[640px]">
      <div className="flex flex-col gap-5">
        <header className="flex flex-col gap-1.5 pr-10">
          <div className="flex items-center gap-2.5">
            <MarkWell platform={channel.platform} label="" />
            <h2 className="font-section text-[20px] font-extrabold leading-tight text-co-text">{name}</h2>
          </div>
          {status ? <p className={cn("text-[13px]", toneClass(status.tone))}>{status.text}</p> : null}
          {/* Posted by hand (its link was pasted), not by the dispatcher. */}
          {post.postedVia === "LINK" ? (
            <p className="mt-1 flex items-center gap-2 text-[12.5px] leading-[1.4] text-co-text2">
              <Info className="size-[15px] shrink-0 text-co-muted" strokeWidth={1.8} aria-hidden="true" />
              This was published outside CreativeOS.
            </p>
          ) : null}
        </header>

        {numbers.length ? (
          <dl className="flex flex-wrap gap-x-7 gap-y-3">
            {numbers.map((entry) => {
              const Icon = METRIC_ICON[entry.kind];
              return (
                <div key={entry.kind} className="flex flex-col-reverse gap-0.5">
                  <dt className="inline-flex items-center gap-1 text-[12px] text-co-muted">
                    <Icon className="size-3" strokeWidth={1.8} aria-hidden="true" />
                    {METRIC_WORD[entry.kind]}
                  </dt>
                  <dd className="text-[22px] font-semibold tabular-nums text-co-text">{compactNumber(entry.value)}</dd>
                </div>
              );
            })}
          </dl>
        ) : reading ? (
          <div className="flex gap-6" aria-hidden="true">
            {[0, 1, 2].map((index) => (
              <span key={index} className="h-[44px] w-14 animate-pulse rounded-tag bg-[var(--co-tonal)] motion-reduce:animate-none" />
            ))}
          </div>
        ) : !post.tracking ? (
          <p className="text-[13px] text-co-muted">CreativeOS can't read this platform's numbers.</p>
        ) : null}

        {post.tracking ? (
          <LiveNumbersChart slotId={slotId} platforms={chartPlatforms(data, channel.platform)} timeZone={timeZone} perPlatform height={240} />
        ) : null}

        {error ? <p className="text-[12.5px] leading-snug text-co-alert-ink">{error}</p> : null}
        {canChange ? <ChangeLink key={post.permalink ?? ""} name={name} platform={platform} current={post.permalink} onSetLink={onSetLink!} /> : null}

        <footer className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-co-line2 pt-4 text-[12.5px] text-co-muted">
          <span className="min-w-0" aria-live="polite">
            {[line, numbers.length ? source : null].filter(Boolean).join(" · ")}
          </span>
          <div className="flex shrink-0 items-center gap-2">
            {onRefresh ? (
              <Button size="small" variant="secondary" icon={RefreshCw} iconClassName={cn("size-3.5", reading && "animate-spin motion-reduce:animate-none")} isDisabled={reading} onPress={onRefresh}>
                Refresh
              </Button>
            ) : null}
            {post.permalink ? (
              <a
                href={post.permalink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-8 items-center gap-1 px-2 text-[13px] text-co-text2 outline-none hover:text-co-text focus-visible:outline-2 focus-visible:outline-co-accent"
              >
                <ArrowUpRight className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
                Open
                <span className="sr-only"> the {name} post</span>
              </a>
            ) : null}
          </div>
        </footer>
      </div>
    </KitModal>
  );
}

/** Change link: opens on the current link; saving reads the numbers from the new one. */
function ChangeLink({
  name,
  platform,
  current,
  onSetLink,
}: {
  name: string;
  platform: string;
  current: string | null;
  onSetLink: (url: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(current ?? "");
  const trimmed = url.trim();
  const close = () => {
    setOpen(false);
    setUrl(current ?? "");
  };

  if (!open) {
    return (
      <Button size="small" variant="secondary" icon={Link2} className="self-start" onPress={() => setOpen(true)}>
        Change link
      </Button>
    );
  }
  return (
    <form
      className="flex flex-col gap-2.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (!trimmed || trimmed === current) return;
        onSetLink(trimmed);
        close();
      }}
    >
      <input
        type="url"
        inputMode="url"
        // Opened by the person's own press, so the field takes the focus.
        autoFocus
        aria-label={`The link to the ${name} post`}
        placeholder={`Paste the ${platform} post's link`}
        value={url}
        onChange={(event) => setUrl(event.target.value)}
        className="h-11 w-full rounded-card border border-co-line2 bg-transparent px-3.5 text-[16px] text-co-text outline-none placeholder:text-co-muted focus:border-co-accent md:text-[14px]"
      />
      <p className="text-[12.5px] leading-relaxed text-co-muted">
        Its numbers are read from this link instead, every hour for a day from now, then daily for a week.
      </p>
      <div className="flex gap-2.5">
        <Button size="small" type="submit" isDisabled={!trimmed || trimmed === current}>
          Save link
        </Button>
        <Button size="small" variant="ghost" onPress={close}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
